/**
 * POI category picker options — kept in sync with GET /map/categories (map.py subcategory_meta + category_groups).
 * Transport-stop types are excluded here; use feature_type transport_stop instead.
 */

export interface ContributionCategoryOption {
  id: string;
  name: string;
  icon: string;
  color: string;
  hint?: string;
}

/** Categories routed to public_transport_stop — never shown on the POI picker. */
export const POI_TRANSPORT_CATEGORY_IDS = new Set([
  'bus_station',
  'bus_stop',
  'trotro',
  'taxi',
  'taxi_rank',
  'ferry_terminal',
  'rail_station',
]);

/** Order matches map.py category_groups (flattened). */
export const POI_API_CATEGORY_ORDER: string[] = [
  // food_drink
  'restaurant', 'fast_food', 'cafe', 'bar', 'pub', 'bakery', 'supermarket', 'convenience', 'food_court', 'ice_cream',
  // financial
  'bank', 'atm', 'bureau_de_change', 'money_transfer',
  // healthcare
  'hospital', 'clinic', 'doctor', 'dentist', 'pharmacy', 'veterinary',
  // education
  'school', 'kindergarten', 'college', 'university', 'library',
  // transport (POI-eligible only — stops filtered below)
  'fuel', 'parking', 'car_rental', 'car_repair',
  // accommodation
  'hotel', 'motel', 'hostel', 'guest_house',
  // shopping
  'mall', 'marketplace', 'clothes', 'electronics', 'furniture',
  // government
  'townhall', 'courthouse', 'police', 'fire_station', 'post_office', 'government', 'embassy',
  // religious
  'place_of_worship',
  // services
  'shop', 'studio', 'hairdresser', 'laundry', 'mobile_money_agent', 'microfinance', 'company', 'general', 'social_facility',
  // recreation
  'park', 'stadium', 'sports_centre', 'pitch', 'beach_resort', 'community_centre', 'cinema', 'nightclub',
];

/** Mirror map.py subcategory_meta (icon / color / display name). */
const POI_API_CATEGORY_DEFS: Record<string, Omit<ContributionCategoryOption, 'id'>> = {
  restaurant: { name: 'Restaurant', icon: 'utensils', color: 'orange' },
  fast_food: { name: 'Fast Food', icon: 'hamburger', color: 'orange' },
  cafe: { name: 'Cafe', icon: 'coffee', color: 'amber' },
  bar: { name: 'Bar', icon: 'beer', color: 'amber' },
  pub: { name: 'Pub', icon: 'beer', color: 'amber' },
  bakery: { name: 'Bakery', icon: 'croissant', color: 'orange' },
  supermarket: { name: 'Supermarket', icon: 'shopping-cart', color: 'green' },
  convenience: { name: 'Convenience Store', icon: 'store', color: 'green' },
  food_court: { name: 'Food Court', icon: 'utensils-crossed', color: 'orange' },
  ice_cream: { name: 'Ice Cream', icon: 'ice-cream-cone', color: 'pink' },
  bank: { name: 'Bank', icon: 'landmark', color: 'blue' },
  atm: { name: 'ATM', icon: 'credit-card', color: 'blue' },
  bureau_de_change: { name: 'Currency Exchange', icon: 'banknote', color: 'green' },
  money_transfer: { name: 'Money Transfer', icon: 'send', color: 'blue' },
  hospital: { name: 'Hospital', icon: 'hospital', color: 'red' },
  clinic: { name: 'Clinic', icon: 'stethoscope', color: 'red' },
  doctor: { name: 'Doctor', icon: 'user-round', color: 'red' },
  dentist: { name: 'Dentist', icon: 'smile', color: 'cyan' },
  pharmacy: { name: 'Pharmacy', icon: 'pill', color: 'green' },
  veterinary: { name: 'Veterinary', icon: 'paw-print', color: 'teal' },
  school: { name: 'School', icon: 'school', color: 'indigo' },
  kindergarten: { name: 'Kindergarten', icon: 'baby', color: 'pink' },
  college: { name: 'College', icon: 'book-open', color: 'indigo' },
  university: { name: 'University', icon: 'graduation-cap', color: 'purple' },
  library: { name: 'Library', icon: 'library', color: 'amber' },
  fuel: { name: 'Fuel Station', icon: 'fuel', color: 'red' },
  parking: { name: 'Parking', icon: 'parking-circle', color: 'blue' },
  car_rental: { name: 'Car Rental', icon: 'car', color: 'blue' },
  car_repair: { name: 'Car Repair', icon: 'wrench', color: 'slate' },
  hotel: { name: 'Hotel', icon: 'hotel', color: 'violet' },
  motel: { name: 'Motel', icon: 'bed-double', color: 'violet' },
  hostel: { name: 'Hostel', icon: 'bed-single', color: 'violet' },
  guest_house: { name: 'Guest House', icon: 'home', color: 'violet' },
  mall: { name: 'Shopping Mall', icon: 'shopping-bag', color: 'pink' },
  marketplace: { name: 'Marketplace', icon: 'store', color: 'emerald' },
  clothes: { name: 'Clothing Store', icon: 'shirt', color: 'rose' },
  electronics: { name: 'Electronics', icon: 'smartphone', color: 'slate' },
  furniture: { name: 'Furniture', icon: 'sofa', color: 'amber' },
  townhall: { name: 'Town Hall', icon: 'building-2', color: 'slate' },
  courthouse: { name: 'Courthouse', icon: 'scale', color: 'slate' },
  police: { name: 'Police Station', icon: 'shield', color: 'blue' },
  fire_station: { name: 'Fire Station', icon: 'flame', color: 'red' },
  post_office: { name: 'Post Office', icon: 'mail', color: 'red' },
  government: { name: 'Government Office', icon: 'landmark', color: 'blue' },
  embassy: { name: 'Embassy', icon: 'flag', color: 'blue' },
  place_of_worship: { name: 'Place of Worship', icon: 'church', color: 'purple' },
  shop: { name: 'Shop', icon: 'store', color: 'emerald' },
  studio: { name: 'Studio', icon: 'camera', color: 'violet' },
  hairdresser: { name: 'Hairdresser / Salon', icon: 'scissors', color: 'rose' },
  laundry: { name: 'Laundry', icon: 'shirt', color: 'cyan' },
  mobile_money_agent: { name: 'Mobile Money', icon: 'smartphone', color: 'green' },
  microfinance: { name: 'Microfinance', icon: 'banknote', color: 'teal' },
  company: { name: 'Company / Office', icon: 'briefcase', color: 'slate' },
  general: { name: 'General', icon: 'map-pin', color: 'gray' },
  social_facility: { name: 'Social Facility', icon: 'heart-handshake', color: 'rose' },
  park: { name: 'Park', icon: 'trees', color: 'green' },
  stadium: { name: 'Stadium', icon: 'circle-dot', color: 'emerald' },
  sports_centre: { name: 'Sports Centre', icon: 'dumbbell', color: 'emerald' },
  pitch: { name: 'Sports Pitch', icon: 'footprints', color: 'lime' },
  beach_resort: { name: 'Beach Resort', icon: 'umbrella', color: 'cyan' },
  community_centre: { name: 'Community Centre', icon: 'users', color: 'teal' },
  cinema: { name: 'Cinema', icon: 'clapperboard', color: 'violet' },
  nightclub: { name: 'Nightclub', icon: 'music', color: 'purple' },
};

export const POI_SUPPLEMENTAL_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'bench', name: 'Bench', icon: 'armchair', color: 'stone' },
  { id: 'waste_basket', name: 'Litter Bin', icon: 'trash-2', color: 'gray' },
];

export function buildPoiCategoryOptions(): ContributionCategoryOption[] {
  const options: ContributionCategoryOption[] = [];
  for (const id of POI_API_CATEGORY_ORDER) {
    if (POI_TRANSPORT_CATEGORY_IDS.has(id)) continue;
    const meta = POI_API_CATEGORY_DEFS[id];
    if (!meta) continue;
    options.push({ id, ...meta });
  }
  const seen = new Set(options.map((c) => c.id));
  for (const extra of POI_SUPPLEMENTAL_CATEGORIES) {
    if (!seen.has(extra.id)) options.push(extra);
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

/** Offline fallback when /map/categories is empty or unreachable. */
export const POI_FALLBACK_CATEGORIES: ContributionCategoryOption[] = buildPoiCategoryOptions();
